/**
 * SFrame163 — Pets & Dependents (secure-inventory si-7)
 * Consolidated settlement page: category tiles → modal → grouped list view.
 * Three categories: Pets, Dependents, Other Care Responsibilities.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import Modal from '../../ui/Modal';
import { SettlementStepPage, type StepStatus } from '../../settlement';
import DocumentUploadSection from '../../settlement/DocumentUploadSection';
import { SETTLEMENT_PHASE_CONFIGS } from '../../../utils/settlementFlowConfig';
import { useSettlementSession } from '../../../hooks/useSettlementSession';
import { useSettlement } from '../../../contexts/SettlementContext';
import {
  deleteSettlementDocument,
  listSettlementDocuments,
  toDocumentUploadRow,
  uploadSettlementDocument,
  type SettlementDocumentRow,
} from '../../../utils/settlementDocuments';

const step = SETTLEMENT_PHASE_CONFIGS['secure-inventory'].steps.find((s) => s.id === 'si-7')!;
const PHASE = 'secure-inventory' as const;
const STEP_ID = step.id;
const FRAME_ID = 'sframe-163';

/* ── types ── */

type CareCategory = 'pet' | 'dependent' | 'other';
type RowStatus = 'needs-review' | 'in-progress' | 'complete';

export interface PersistedItem {
  id: string;
  category: CareCategory;
  name: string;
  /** Pet animal type OR dependent relationship; empty for 'other' */
  subtype: string;
  age: string;
  specialNeeds: string;
  /** Caregiver / Guardian / Responsible party */
  caregiverName: string;
  contact: string;
  /** Care instructions for pets/dependents; general notes for other */
  careInstructions: string;
  status: RowStatus;
  documentIds: string[];
}

/** id is always pre-assigned so docs can be uploaded during modal before the item is saved */
type ItemDraft = Omit<PersistedItem, 'documentIds'>;

/* ── constants ── */

const PET_TYPES = ['Dog', 'Cat', 'Bird', 'Other'] as const;
const DEPENDENT_RELATIONSHIPS = ['Child', 'Parent', 'Sibling', 'Other'] as const;

interface CategoryDef {
  key: CareCategory;
  label: string;
  description: string;
  subtypeLabel: string | null;
  subtypeOptions: readonly string[] | null;
  caregiverLabel: string;
  instructionsLabel: string;
  instructionsPlaceholder: string;
  namePlaceholder: string;
}

const CATEGORY_DEFS: CategoryDef[] = [
  {
    key: 'pet',
    label: 'Pets',
    description: 'Dogs, cats, and other animals requiring care.',
    subtypeLabel: 'Type',
    subtypeOptions: PET_TYPES,
    caregiverLabel: 'Caregiver',
    instructionsLabel: 'Care instructions',
    instructionsPlaceholder: 'Routines, feeding schedule, vet contact, medications...',
    namePlaceholder: 'e.g. Bella',
  },
  {
    key: 'dependent',
    label: 'Dependents',
    description: 'Children, parents, or others relying on the estate for care.',
    subtypeLabel: 'Relationship',
    subtypeOptions: DEPENDENT_RELATIONSHIPS,
    caregiverLabel: 'Guardian',
    instructionsLabel: 'Care instructions',
    instructionsPlaceholder: 'Routines, school, doctor contact, medications...',
    namePlaceholder: 'Full name',
  },
  {
    key: 'other',
    label: 'Other Care Responsibilities',
    description: 'Any additional individuals or responsibilities not captured above.',
    subtypeLabel: null,
    subtypeOptions: null,
    caregiverLabel: 'Responsible party',
    instructionsLabel: 'Notes',
    instructionsPlaceholder: 'Any relevant details or instructions',
    namePlaceholder: 'Name or description',
  },
];

/* ── helpers ── */

function nid() {
  return `id-${Math.random().toString(36).slice(2, 11)}`;
}

function getCategoryDef(key: CareCategory): CategoryDef {
  return CATEGORY_DEFS.find((c) => c.key === key)!;
}

function emptyDraft(category: CareCategory): ItemDraft {
  const def = getCategoryDef(category);
  return {
    id: nid(),
    category,
    name: '',
    subtype: def.subtypeOptions?.[0] ?? '',
    age: '',
    specialNeeds: '',
    caregiverName: '',
    contact: '',
    careInstructions: '',
    status: 'needs-review',
  };
}

function statusLabel(s: RowStatus) {
  if (s === 'complete') return 'Completed';
  if (s === 'in-progress') return 'In Progress';
  return 'Needs review';
}

function statusBadgeClass(s: RowStatus) {
  if (s === 'complete') return 'border-emerald-300 bg-emerald-50 text-emerald-900';
  if (s === 'in-progress') return 'border-amber-300 bg-amber-50 text-amber-900';
  return 'border-stone-200 bg-stone-100 text-stone-700';
}

function cycleRowStatus(s: RowStatus): RowStatus {
  if (s === 'needs-review') return 'in-progress';
  if (s === 'in-progress') return 'complete';
  return 'needs-review';
}

function deriveStepStatus(
  items: PersistedItem[],
  notesNonEmpty: boolean,
  miscDocCount: number
): StepStatus {
  if (items.length === 0 && !notesNonEmpty && miscDocCount === 0) return 'not-started';
  if (items.length > 0 && items.every((i) => i.status === 'complete')) return 'complete';
  return 'in-progress';
}

function modalTitle(draft: ItemDraft): string {
  const def = getCategoryDef(draft.category);
  // "Other Care Responsibilities" → singular "other care responsibility"
  const singular =
    draft.category === 'other'
      ? 'other care responsibility'
      : def.label.toLowerCase().replace(/s$/, '');
  return draft.id ? `Edit ${singular}` : `Add ${singular}`;
}

/* ── icons ── */

function PencilIcon() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
      />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
      />
    </svg>
  );
}

/* ── legacy migration ── */

interface LegacyDependent {
  id?: string;
  name?: string;
  type?: string;
  caregiverName?: string;
  caregiverContact?: string;
  careInstructions?: string;
  status?: string;
}

function migrateLegacy(dependents: LegacyDependent[]): PersistedItem[] {
  return dependents.map((d) => {
    const legacyType = String(d.type ?? '');
    const isPet = legacyType === 'Pet';
    const category: CareCategory = isPet ? 'pet' : 'dependent';
    const subtype = isPet
      ? 'Other'
      : legacyType.replace(' Dependent', '').trim() || 'Child';
    const rawStatus = String(d.status ?? '');
    const status: RowStatus =
      rawStatus === 'complete' || rawStatus === 'in-progress' || rawStatus === 'needs-review'
        ? rawStatus
        : 'needs-review';
    return {
      id: String(d.id ?? nid()),
      category,
      name: String(d.name ?? ''),
      subtype,
      age: '',
      specialNeeds: '',
      caregiverName: String(d.caregiverName ?? ''),
      contact: String(d.caregiverContact ?? ''),
      careInstructions: String(d.careInstructions ?? ''),
      status,
      documentIds: [],
    };
  });
}

/* ── component ── */

export default function SFrame163Pets() {
  const { estateId } = useParams<{ estateId: string }>();
  const { isArchived } = useSettlement();
  const { loadFrameData, saveFrameData, updateStepProgress, loadPhaseProgress } =
    useSettlementSession(estateId ?? null, { readOnly: isArchived });

  const [items, setItems] = useState<PersistedItem[]>([]);
  const [allStepDocs, setAllStepDocs] = useState<SettlementDocumentRow[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [draft, setDraft] = useState<ItemDraft>(emptyDraft('pet'));
  /** Document IDs attached to the item currently being added/edited in the modal */
  const [modalDocIds, setModalDocIds] = useState<string[]>([]);
  const [stepStatus, setStepStatus] = useState<StepStatus>('not-started');

  const isManualOverride = useRef(false);
  const stepStatusRef = useRef<StepStatus>('not-started');

  /* ── persistence ── */

  const persist = useCallback(
    async (next: PersistedItem[]) => {
      if (!estateId || isArchived) return;
      await saveFrameData(FRAME_ID, { items: next });
    },
    [estateId, isArchived, saveFrameData]
  );

  const applyDerivedStatus = useCallback(
    (nextItems: PersistedItem[]) => {
      if (isManualOverride.current) return;
      const derived = deriveStepStatus(nextItems, false, 0);
      if (derived !== stepStatusRef.current) {
        stepStatusRef.current = derived;
        setStepStatus(derived);
        if (estateId && !isArchived) void updateStepProgress(STEP_ID, PHASE, { status: derived });
      }
    },
    [estateId, isArchived, updateStepProgress]
  );

  const handleStatusChange = useCallback(
    (status: StepStatus) => {
      isManualOverride.current = true;
      stepStatusRef.current = status;
      setStepStatus(status);
      if (estateId && !isArchived) void updateStepProgress(STEP_ID, PHASE, { status });
    },
    [estateId, isArchived, updateStepProgress]
  );

  /* ── load ── */

  useEffect(() => {
    if (!estateId) return;
    let cancelled = false;
    (async () => {
      const [fd, progressSteps] = await Promise.all([
        loadFrameData(FRAME_ID),
        loadPhaseProgress(PHASE),
      ]);
      if (cancelled) return;

      const raw = (fd?.data ?? {}) as {
        items?: PersistedItem[];
        dependents?: LegacyDependent[];
      };

      if (Array.isArray(raw.items)) {
        setItems(raw.items);
      } else if (Array.isArray(raw.dependents)) {
        setItems(migrateLegacy(raw.dependents));
      }

      const thisStep = progressSteps.find((s) => s.stepId === STEP_ID);
      if (thisStep) {
        const loaded = (thisStep.status as StepStatus) || 'not-started';
        stepStatusRef.current = loaded;
        setStepStatus(loaded);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [estateId, loadFrameData, loadPhaseProgress]);

  /* ── document management ── */

  const refreshDocs = useCallback(async () => {
    if (!estateId) return;
    const rows = await listSettlementDocuments(estateId, PHASE, STEP_ID);
    setAllStepDocs(rows);
  }, [estateId]);

  useEffect(() => {
    void refreshDocs();
  }, [refreshDocs]);

  const docsForItem = useCallback(
    (itemId: string, documentIds: string[]) => {
      const wanted = new Set(documentIds);
      return allStepDocs.filter((d) => wanted.has(d.id)).map(toDocumentUploadRow);
    },
    [allStepDocs]
  );

  const handleItemDocUpload = async (itemId: string, file: File) => {
    if (!estateId || isArchived) return;
    const uploaded = await uploadSettlementDocument({
      estateId,
      phase: PHASE,
      stepId: STEP_ID,
      file,
      description: `item:${itemId}`,
    });
    await refreshDocs();
    setItems((prev) => {
      const next = prev.map((i) =>
        i.id === itemId ? { ...i, documentIds: [...i.documentIds, uploaded.id] } : i
      );
      void persist(next);
      return next;
    });
  };

  const handleItemDocDelete = async (itemId: string, docId: string) => {
    if (!estateId || isArchived) return;
    await deleteSettlementDocument(estateId, docId);
    await refreshDocs();
    setItems((prev) => {
      const next = prev.map((i) =>
        i.id === itemId
          ? { ...i, documentIds: i.documentIds.filter((x) => x !== docId) }
          : i
      );
      void persist(next);
      return next;
    });
  };

  /* ── modal document handlers ── */

  const handleModalDocUpload = useCallback(
    async (file: File) => {
      if (!estateId || isArchived) return;
      const uploaded = await uploadSettlementDocument({
        estateId,
        phase: PHASE,
        stepId: STEP_ID,
        file,
        description: `item:${draft.id}`,
      });
      await refreshDocs();
      setModalDocIds((prev) => [...prev, uploaded.id]);
    },
    [estateId, isArchived, draft.id, refreshDocs]
  );

  const handleModalDocDelete = useCallback(
    async (docId: string) => {
      if (!estateId || isArchived) return;
      await deleteSettlementDocument(estateId, docId);
      await refreshDocs();
      setModalDocIds((prev) => prev.filter((x) => x !== docId));
    },
    [estateId, isArchived, refreshDocs]
  );

  /* ── modal actions ── */

  const openAdd = (category: CareCategory) => {
    setDraft(emptyDraft(category));
    setModalDocIds([]);
    setModalOpen(true);
  };

  const openEdit = (item: PersistedItem) => {
    setDraft({
      id: item.id,
      category: item.category,
      name: item.name,
      subtype: item.subtype,
      age: item.age,
      specialNeeds: item.specialNeeds,
      caregiverName: item.caregiverName,
      contact: item.contact,
      careInstructions: item.careInstructions,
      status: item.status,
    });
    setModalDocIds(item.documentIds);
    setModalOpen(true);
  };

  const saveFromDraft = useCallback(() => {
    const exists = items.some((i) => i.id === draft.id);
    const row: PersistedItem = {
      id: draft.id,
      category: draft.category,
      name: draft.name.trim() || 'Unnamed',
      subtype: draft.subtype,
      age: draft.age.trim(),
      specialNeeds: draft.specialNeeds.trim(),
      caregiverName: draft.caregiverName.trim(),
      contact: draft.contact.trim(),
      careInstructions: draft.careInstructions.trim(),
      status: draft.status,
      documentIds: modalDocIds,
    };
    setItems((prev) => {
      const next = exists
        ? prev.map((i) => (i.id === row.id ? row : i))
        : [...prev, row];
      void persist(next);
      applyDerivedStatus(next);
      return next;
    });
    setModalOpen(false);
  }, [draft, modalDocIds, items, persist, applyDerivedStatus]);

  const deleteItem = (id: string) => {
    if (!window.confirm('Remove this entry?')) return;
    setItems((prev) => {
      const next = prev.filter((i) => i.id !== id);
      void persist(next);
      applyDerivedStatus(next);
      return next;
    });
  };

  const cycleItemStatus = (id: string) => {
    if (isArchived) return;
    setItems((prev) => {
      const next = prev.map((i) =>
        i.id === id ? { ...i, status: cycleRowStatus(i.status) } : i
      );
      void persist(next);
      applyDerivedStatus(next);
      return next;
    });
  };

  /* ── render ── */

  const def = getCategoryDef(draft.category);

  return (
    <SettlementStepPage
      step={step}
      phase="secure-inventory"
      overviewRoute="/sframe-143"
      statusControl="segmented"
      externalStatus={stepStatus}
      onStatusChange={handleStatusChange}
      hideNotes
      hideDocuments
    >
      {/* Category tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
        {CATEGORY_DEFS.map((cat) => {
          const count = items.filter((i) => i.category === cat.key).length;
          return (
            <button
              key={cat.key}
              type="button"
              disabled={!!isArchived}
              onClick={() => openAdd(cat.key)}
              className="text-left rounded-xl border border-stone-200 bg-white p-5 shadow-sm hover:border-cyan-400 hover:shadow transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <p className="text-base font-semibold text-stone-900">{cat.label}</p>
              <p className="text-sm text-stone-500 mt-1">{cat.description}</p>
              {count > 0 ? (
                <p className="mt-3 text-xs font-semibold text-cyan-700">
                  {count} {count === 1 ? 'entry' : 'entries'}
                </p>
              ) : (
                !isArchived && (
                  <p className="mt-3 text-xs font-semibold text-cyan-600">+ Add entry</p>
                )
              )}
            </button>
          );
        })}
      </div>

      {/* Grouped list */}
      {items.length === 0 ? (
        <p className="text-sm text-stone-500 italic border border-stone-200 rounded-xl p-5 mb-6">
          No entries yet. Select a category above to add one.
        </p>
      ) : (
        <div className="space-y-10 mb-6">
          {CATEGORY_DEFS.map((cat) => {
            const catItems = items.filter((i) => i.category === cat.key);
            if (catItems.length === 0) return null;
            const cDef = getCategoryDef(cat.key);
            return (
              <section key={cat.key}>
                <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-3">
                  {cat.label}
                </p>
                <div className="space-y-4">
                  {catItems.map((item) => (
                    <div
                      key={item.id}
                      className="rounded-xl border border-stone-200 bg-white shadow-sm p-5"
                    >
                      {/* Card header */}
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-base font-semibold text-stone-900">{item.name}</p>
                          {item.subtype && (
                            <p className="text-sm text-stone-500 mt-0.5">{item.subtype}</p>
                          )}
                        </div>
                        {!isArchived && (
                          <div className="flex items-center gap-2 shrink-0">
                            <button
                              type="button"
                              onClick={() => openEdit(item)}
                              className="p-2 rounded-lg border border-stone-200 text-stone-600 hover:bg-stone-50"
                              aria-label="Edit entry"
                            >
                              <PencilIcon />
                            </button>
                            <button
                              type="button"
                              onClick={() => deleteItem(item.id)}
                              className="p-2 rounded-lg border border-stone-200 text-stone-600 hover:bg-red-50 hover:text-red-700"
                              aria-label="Delete entry"
                            >
                              <TrashIcon />
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Detail rows */}
                      <div className="mt-3 text-sm text-stone-700 space-y-0.5">
                        {item.caregiverName && (
                          <p>
                            <span className="text-stone-500">{cDef.caregiverLabel}:</span>{' '}
                            {item.caregiverName}
                          </p>
                        )}
                        {item.contact && (
                          <p>
                            <span className="text-stone-500">Contact:</span> {item.contact}
                          </p>
                        )}
                      </div>

                      {(item.age || item.specialNeeds) && (
                        <p className="mt-1.5 text-xs text-stone-500">
                          {item.age ? `Age: ${item.age}` : ''}
                          {item.age && item.specialNeeds ? ' · ' : ''}
                          {item.specialNeeds ? `Special needs: ${item.specialNeeds}` : ''}
                        </p>
                      )}

                      {item.careInstructions && (
                        <p className="mt-2 text-xs text-stone-600 whitespace-pre-line">
                          <span className="font-semibold">{cDef.instructionsLabel}:</span>{' '}
                          {item.careInstructions}
                        </p>
                      )}

                      {/* Status badge */}
                      <div className="mt-3">
                        <button
                          type="button"
                          disabled={!!isArchived}
                          onClick={() => cycleItemStatus(item.id)}
                          className="rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-600 disabled:opacity-50"
                          title="Cycle status"
                        >
                          <span
                            className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${statusBadgeClass(item.status)}`}
                          >
                            {statusLabel(item.status)}
                          </span>
                        </button>
                      </div>

                      {/* Per-item document upload */}
                      {estateId && (
                        <div className="mt-4 pt-4 border-t border-stone-100">
                          <DocumentUploadSection
                            documents={docsForItem(item.id, item.documentIds)}
                            disabled={isArchived || !estateId}
                            onUpload={(file) => handleItemDocUpload(item.id, file)}
                            onDelete={
                              !isArchived
                                ? (docId) => handleItemDocDelete(item.id, docId)
                                : undefined
                            }
                            className="[&_h3]:text-xs"
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}

      {/* Add / Edit modal */}
      <Modal
        open={modalOpen}
        title={modalTitle(draft)}
        onCancel={() => setModalOpen(false)}
        onOk={saveFromDraft}
        okText="Save"
        cancelText="Cancel"
      >
        <div className="space-y-4">
          {/* Name */}
          <label className="block text-sm">
            <span className="text-xs font-semibold text-stone-700">Name</span>
            <input
              className="mt-1 w-full rounded-lg border border-stone-300 px-3 py-2 text-sm"
              value={draft.name}
              disabled={isArchived}
              placeholder={def.namePlaceholder}
              onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
            />
          </label>

          {/* Subtype: pet animal type or dependent relationship */}
          {def.subtypeOptions && def.subtypeLabel && (
            <label className="block text-sm">
              <span className="text-xs font-semibold text-stone-700">{def.subtypeLabel}</span>
              <select
                className="mt-1 w-full rounded-lg border border-stone-300 px-3 py-2 text-sm"
                value={draft.subtype}
                disabled={isArchived}
                onChange={(e) => setDraft((d) => ({ ...d, subtype: e.target.value }))}
              >
                {def.subtypeOptions.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </label>
          )}

          {/* Age — pets and dependents only */}
          {draft.category !== 'other' && (
            <label className="block text-sm">
              <span className="text-xs font-semibold text-stone-700">
                Age{' '}
                <span className="font-normal text-stone-400">(optional)</span>
              </span>
              <input
                className="mt-1 w-full rounded-lg border border-stone-300 px-3 py-2 text-sm"
                value={draft.age}
                disabled={isArchived}
                placeholder="e.g. 5, or 3 years old"
                onChange={(e) => setDraft((d) => ({ ...d, age: e.target.value }))}
              />
            </label>
          )}

          {/* Special needs — pets and dependents only */}
          {draft.category !== 'other' && (
            <label className="block text-sm">
              <span className="text-xs font-semibold text-stone-700">
                Special needs{' '}
                <span className="font-normal text-stone-400">(optional)</span>
              </span>
              <input
                className="mt-1 w-full rounded-lg border border-stone-300 px-3 py-2 text-sm"
                value={draft.specialNeeds}
                disabled={isArchived}
                placeholder="Any medical conditions or special requirements"
                onChange={(e) => setDraft((d) => ({ ...d, specialNeeds: e.target.value }))}
              />
            </label>
          )}

          {/* Caregiver / Guardian / Responsible party */}
          <label className="block text-sm">
            <span className="text-xs font-semibold text-stone-700">{def.caregiverLabel}</span>
            <input
              className="mt-1 w-full rounded-lg border border-stone-300 px-3 py-2 text-sm"
              value={draft.caregiverName}
              disabled={isArchived}
              placeholder={`Full name of ${def.caregiverLabel.toLowerCase()}`}
              onChange={(e) => setDraft((d) => ({ ...d, caregiverName: e.target.value }))}
            />
          </label>

          {/* Contact */}
          <label className="block text-sm">
            <span className="text-xs font-semibold text-stone-700">Contact information</span>
            <input
              className="mt-1 w-full rounded-lg border border-stone-300 px-3 py-2 text-sm"
              value={draft.contact}
              disabled={isArchived}
              placeholder="Phone number or email"
              onChange={(e) => setDraft((d) => ({ ...d, contact: e.target.value }))}
            />
          </label>

          {/* Care instructions / Notes */}
          <label className="block text-sm">
            <span className="text-xs font-semibold text-stone-700">{def.instructionsLabel}</span>
            <textarea
              className="mt-1 w-full rounded-lg border border-stone-300 px-3 py-2 text-sm min-h-[80px]"
              value={draft.careInstructions}
              disabled={isArchived}
              placeholder={def.instructionsPlaceholder}
              onChange={(e) => setDraft((d) => ({ ...d, careInstructions: e.target.value }))}
            />
          </label>

          {/* Status */}
          <label className="block text-sm">
            <span className="text-xs font-semibold text-stone-700">Status</span>
            <select
              className="mt-1 w-full rounded-lg border border-stone-300 px-3 py-2 text-sm"
              value={draft.status}
              disabled={isArchived}
              onChange={(e) => setDraft((d) => ({ ...d, status: e.target.value as RowStatus }))}
            >
              <option value="needs-review">Needs review</option>
              <option value="in-progress">In Progress</option>
              <option value="complete">Completed</option>
            </select>
          </label>

          {/* Per-item document upload inside the modal */}
          {estateId && (
            <div>
              <p className="text-xs font-semibold text-stone-700 mb-1">
                Documents{' '}
                <span className="font-normal text-stone-400">(optional)</span>
              </p>
              <DocumentUploadSection
                documents={docsForItem(draft.id, modalDocIds)}
                disabled={isArchived || !estateId}
                onUpload={handleModalDocUpload}
                onDelete={!isArchived ? handleModalDocDelete : undefined}
                className="[&_h3]:hidden"
              />
            </div>
          )}
        </div>
      </Modal>
    </SettlementStepPage>
  );
}
